import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionService } from 'src/app/services/permission.service';
import { portalCommands } from 'src/app/utils/portal-path';
import * as QRCode from 'qrcode';
import { ParentsService } from 'src/app/services/parents.service';
import { LanguageService } from 'src/app/services/language.service';

@Component({
  selector: 'app-parent-profile',
  templateUrl: './parent-profile.component.html',
  styleUrls: ['./parent-profile.component.css']
})
export class ParentProfileComponent {
  parent: any = null;
  loading = true;
  errorMessage = '';
  qrDataUrl = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private parentsService: ParentsService,
    public languageService: LanguageService,
    public permissions: PermissionService,
  ) {}

  async ngOnInit() {
    this.route.paramMap.subscribe(async params => {
      const id = params.get('id');
      if (!id) {
        this.router.navigate(portalCommands(this.router, 'settings', 'parents'));
        return;
      }
      await this.loadParent(id);
    });
  }

  async loadParent(id: string) {
    this.loading = true;
    this.errorMessage = '';
    this.qrDataUrl = '';
    try {
      this.parent = await this.parentsService.getById(id);
      if (!this.parent) {
        this.errorMessage = 'PARENTS.NOT_FOUND';
      } else {
        await this.refreshQrCode();
      }
    } catch {
      this.errorMessage = 'PARENTS.LOAD_FAILED';
      this.parent = null;
    } finally {
      this.loading = false;
    }
  }

  private async refreshQrCode() {
    const code = String(this.parent?.qrCode || '').trim();
    if (!code) {
      this.qrDataUrl = '';
      return;
    }
    try {
      this.qrDataUrl = await QRCode.toDataURL(code, {
        width: 168,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: { dark: '#173654', light: '#ffffff' },
      });
    } catch {
      this.qrDataUrl = '';
    }
  }

  get parentsLink(): string[] {
    return portalCommands(this.router, 'settings', 'parents');
  }

  goBack() {
    this.router.navigate(portalCommands(this.router, 'settings', 'parents'));
  }

  onEdit() {
    if (!this.parent?.id) {
      return;
    }
    this.router.navigate(portalCommands(this.router, 'settings', 'parents'), {
      queryParams: { edit: this.parent.id },
    });
  }

  openStudent(student: any) {
    if (!student?.id || !this.permissions.canView('students')) {
      return;
    }
    this.router.navigate(portalCommands(this.router, 'settings', 'students', student.id));
  }

  get nameAr(): string {
    if (!this.parent) {
      return '';
    }
    return `${this.parent.firstNameAr || ''} ${this.parent.lastNameAr || ''}`.trim();
  }

  get nameEn(): string {
    if (!this.parent) {
      return '';
    }
    return `${this.parent.firstNameEn || ''} ${this.parent.lastNameEn || ''}`.trim();
  }

  get initials(): string {
    const parts = this.nameAr.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last) || 'و';
  }

  get students(): any[] {
    return this.parent?.students || [];
  }

  get phones(): any[] {
    return this.parent?.phones || [];
  }

  get emails(): any[] {
    return this.parent?.emails || [];
  }

  get primaryPhone(): string {
    const primary = this.phones.find((phone: any) => phone.isPrimary) || this.phones[0];
    return primary?.value || '';
  }

  get primaryEmail(): string {
    const primary = this.emails.find((email: any) => email.isPrimary) || this.emails[0];
    return primary?.value || '';
  }

  get emergencyCount(): number {
    return this.students.filter((student: any) => student.isPrimaryEmergency).length;
  }

  studentNameAr(student: any): string {
    if (!student) {
      return '—';
    }
    return `${student.firstNameAr || ''} ${student.lastNameAr || ''}`.trim() || '—';
  }

  studentNameEn(student: any): string {
    if (!student) {
      return '';
    }
    return `${student.firstNameEn || ''} ${student.lastNameEn || ''}`.trim();
  }

  studentInitial(student: any): string {
    return this.studentNameAr(student).replace(/\s+/g, '').slice(0, 1) || 'ط';
  }

  relationKey(relation?: string): string {
    return `STUDENTS.RELATION_${(relation || 'guardian').toUpperCase()}`;
  }

  labelKey(label?: string): string {
    return `STUDENTS.LABEL_${(label || 'mobile').toUpperCase()}`;
  }

  valueOrDash(value: unknown): string {
    if (value == null) {
      return '—';
    }
    const text = String(value).trim();
    return text || '—';
  }
}
